import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireEmailConfirmation?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireEmailConfirmation = true 
}) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [checkingEmail, setCheckingEmail] = React.useState(true);
  const [emailConfirmed, setEmailConfirmed] = React.useState(false);

  React.useEffect(() => {
    const checkEmailConfirmation = async () => {
      if (!user || !requireEmailConfirmation) {
        setCheckingEmail(false);
        setEmailConfirmed(true);
        return;
      }

      try {
        // Récupérer les détails complets de l'utilisateur
        const { data: { user: fullUser }, error } = await supabase.auth.getUser();
        
        if (error || !fullUser) {
          setEmailConfirmed(false);
          setCheckingEmail(false);
          return;
        }

        // Vérifier si l'email est confirmé
        const isConfirmed = !!fullUser.email_confirmed_at;

        setEmailConfirmed(isConfirmed);
      } catch (err) {
        setEmailConfirmed(false);
      } finally {
        setCheckingEmail(false);
      }
    };

    if (!loading) {
      checkEmailConfirmation();
    }
  }, [user, loading, requireEmailConfirmation]);

  if (loading || checkingEmail) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#002c46] mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    // Rediriger vers login en sauvegardant la page demandée
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Vérifier la confirmation d'email si requise
  if (requireEmailConfirmation && !emailConfirmed) {
    return <Navigate to="/email-not-confirmed" replace />;
  }
  
  return <>{children}</>;
}; 